import {
  AppPageHeader,
  EditIcon,
  Group,
  InfiniteTable,
} from '@/components/_common';
import { CheckOutlined } from '@ant-design/icons';
import { useWorkOrdersTags } from '@/core/domain/workorder/WorkOrderTags/WorkOrdersTags.hooks';
import {
  WorkOrderTag,
  WorkOrderTagsQueryFilter,
} from '@/core/domain/workorder/WorkOrderTags/WorkOrdersTags.types';
import { useDebounce, useDisclosure, useWindowSize } from '@/hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { Button, Divider, Modal, ModalProps, Space, Tag } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useState } from 'react';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';
import TagsWorkOrderModal from '../TagsWorkOrderModal';

export interface Props extends Omit<ModalProps, 'onCancel'> {
  onCallback: (tagId: number) => void;
  onCancel: () => void;
}

export default function TagsWorkOrderSelectModal({
  onCallback,
  onCancel,
  ...props
}: Props) {
  const { isMobile } = useWindowSize();

  const [queryParams, setQueryParams] = useState<WorkOrderTagsQueryFilter>({
    order: 'ASC',
  });

  const queryDebounce = useDebounce(queryParams, 500);

  const { data, isFetching, fetchNextPage, hasNextPage } = useWorkOrdersTags(
    queryDebounce,
    props.open,
  );

  const [modalOpened, modalActions] = useDisclosure();
  const [tagSelected, setTagSelected] = useState<WorkOrderTag>();

  const workOrderTags = flatInfiniteData(data);

  async function handleCallback(tagId: number) {
    await onCallback(tagId);

    onCancel();
  }

  const columns: ColumnsType<WorkOrderTag> = [
    {
      dataIndex: 'name',
      title: 'Descrição',
      render: (_, record) => (
        <Tag color={record.color}>{record.name.toUpperCase()}</Tag>
      ),
    },
    {
      dataIndex: 'actions',
      fixed: 'right',
      align: 'right',
      render: (_, row) => (
        <Space direction="horizontal" size="middle" style={{ display: 'flex' }}>
          <EditIcon
            size={24}
            iconProps={{
              onClick: () => {
                setTagSelected(row);
                modalActions.open();
              },
            }}
          />

          <Divider type="vertical" />

          <Button
            type="primary"
            onClick={() => handleCallback(row.id)}
            icon={<CheckOutlined />}
          />
        </Space>
      ),
      width: '20%',
    },
  ];
  return (
    <>
      <Modal
        {...props}
        title="Seleção de etiqueta para o complemento"
        footer={[]}
        width={700}
        onCancel={onCancel}
      >
        {props.confirmLoading && (
          <ModalLoadingOverlay
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              backdropFilter: 'blur(2px)',
            }}
            messages={['Incluindo Etiqueta...']}
          />
        )}

        <Group
          position="between"
          align="center"
          gap={24}
          style={{ flexDirection: isMobile ? 'column' : undefined }}
        >
          <AppPageHeader
            onSearch={(value) =>
              setQueryParams((prev) => ({ ...prev, search: value }))
            }
          />

          <Button
            type="primary"
            onClick={modalActions.open}
            style={{ marginBottom: 24 }}
          >
            Criar Etiqueta
          </Button>
        </Group>

        <InfiniteTable<WorkOrderTag>
          columns={columns}
          dataSource={workOrderTags}
          loading={isFetching}
          rowKey={(row) => row.id}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
        />
      </Modal>

      <TagsWorkOrderModal
        tagDetail={tagSelected}
        open={modalOpened}
        onCancel={() => {
          setTagSelected(undefined);
          modalActions.close();
        }}
      />
    </>
  );
}
